"use client";

import { useMemo, useState } from "react";
import { Plug, SearchX } from "lucide-react";
import BaseDialog from "@/components/Modals/BaseDialog";
import { SearchBar } from "@/components/SearchBar";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { cn } from "@/lib/utils";

export interface MCPPickerCredential {
  id: string;
  display_name: string;
  integration_name: string;
  integration_image: string;
  enabled_tools_count: number;
}

export function MCPServerPickerDialog({
  isOpen,
  onClose,
  available,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  available: MCPPickerCredential[];
  onAdd: (credentialId: string) => void;
}) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return available;
    return available.filter(
      (c) =>
        c.display_name.toLowerCase().includes(q) ||
        c.integration_name.toLowerCase().includes(q)
    );
  }, [available, search]);

  const handleSelect = (id: string) => {
    onAdd(id);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearch("");
      onClose();
    }
  };

  return (
    <BaseDialog
      title="Add MCP"
      description="Pick a workspace MCP connection this agent can call tools on."
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      size="lg"
      disableScroll
      overflowVisible
      footerActions={[
        {
          label: "Cancel",
          props: { variant: "ghost", onClick: onClose },
        },
      ]}
    >
      <div className="flex flex-col gap-3">
        <SearchBar
          placeholder="Search MCP connections"
          value={search}
          setValue={setSearch}
          isDebounced
          autoFocus
        />

        {filtered.length === 0 ? (
          available.length === 0 ? (
            <NoxusEmptyBlock
              title="No MCP connections"
              description="No MCP connections available."
              icon={Plug}
              size="sm"
            />
          ) : (
            <NoxusEmptyBlock
              title="No MCP connections"
              description={
                <>No MCP connections match &ldquo;{search}&rdquo;.</>
              }
              icon={SearchX}
              size="sm"
              button="Clear search"
              buttonVariant="outline"
              onClick={() => setSearch("")}
            />
          )
        ) : (
          <div
            className="flex flex-col gap-2 max-h-[320px] overflow-y-auto overflow-x-hidden -mr-5"
            style={{ scrollbarGutter: "stable" }}
          >
            {filtered.map((cred) => (
              <button
                key={cred.id}
                type="button"
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border border-border bg-card shadow-xs transition-all text-left",
                  "cursor-pointer hover:border-ring hover:shadow-sm"
                )}
                onClick={() => handleSelect(cred.id)}
              >
                <div className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center bg-muted">
                  {cred.integration_image ? (
                    <img
                      src={cred.integration_image}
                      alt={cred.integration_name}
                      className="size-4 object-contain"
                    />
                  ) : (
                    <Plug className="size-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0 text-sm font-medium text-foreground truncate">
                  {cred.display_name}
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {cred.enabled_tools_count} tools
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </BaseDialog>
  );
}
